<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { storeToRefs } from 'pinia';
import { useCaptainConfigStore } from 'dashboard/store/captain/preferences';
import { useLlmCredentialStore } from 'dashboard/store/captain/llmCredentials';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import { providerName, resolveFeatureCredential } from '../llmCredentials';

// The features the page lists, as { key, title }: the ones an admin expects to just work.
const props = defineProps({
  features: { type: Array, required: true },
});

const emit = defineEmits(['add']);

const { t } = useI18n();
const { features: preferences } = storeToRefs(useCaptainConfigStore());
const { records, uiFlags } = storeToRefs(useLlmCredentialStore());

const uncovered = computed(() =>
  props.features
    .map(feature => ({ ...feature, config: preferences.value[feature.key] }))
    .filter(
      feature =>
        feature.config &&
        !resolveFeatureCredential(records.value, feature.config)
    )
);

const exhausted = computed(() =>
  records.value.filter(credential => credential.limit_reached)
);

// The provider refused the last call made on these: no credits, a revoked key, an endpoint down.
const failing = computed(() =>
  records.value.filter(credential => credential.last_error_at)
);

const needsAttention = computed(
  () => uncovered.value.length || exhausted.value.length || failing.value.length
);

const coveredCount = computed(
  () => props.features.length - uncovered.value.length
);

const isEmpty = computed(
  () => !records.value.length && !uiFlags.value.isFetching
);
</script>

<template>
  <div
    v-if="isEmpty"
    class="flex flex-col gap-4 p-5 sm:flex-row sm:items-center rounded-xl bg-n-amber-2 outline outline-1 outline-n-amber-5 -outline-offset-1"
  >
    <span
      class="grid flex-shrink-0 rounded-full size-10 place-content-center bg-n-amber-3 text-n-amber-11"
    >
      <Icon icon="i-lucide-key-round" class="size-5" />
    </span>
    <div class="flex flex-col flex-1 gap-1">
      <h3 class="m-0 text-sm font-semibold text-n-slate-12">
        {{ t('CAPTAIN_SETTINGS.STATUS.OFF_TITLE') }}
      </h3>
      <p class="m-0 text-sm text-n-slate-11">
        {{ t('CAPTAIN_SETTINGS.STATUS.OFF_DESCRIPTION') }}
      </p>
    </div>
    <NextButton
      type="button"
      icon="i-lucide-plus"
      class="flex-shrink-0"
      :label="t('CAPTAIN_SETTINGS.STATUS.ADD_FIRST')"
      @click="emit('add')"
    />
  </div>

  <div
    v-else-if="records.length"
    class="flex flex-col gap-3 p-5 rounded-xl bg-n-solid-1 outline outline-1 outline-n-weak -outline-offset-1"
  >
    <div class="flex flex-wrap items-center gap-x-4 gap-y-1">
      <span
        class="flex items-center gap-2 text-sm font-semibold text-n-slate-12"
      >
        <span
          class="rounded-full size-2"
          :class="needsAttention ? 'bg-n-amber-9' : 'bg-n-teal-9'"
        />
        {{
          needsAttention
            ? t('CAPTAIN_SETTINGS.STATUS.ATTENTION_TITLE')
            : t('CAPTAIN_SETTINGS.STATUS.ON_TITLE')
        }}
      </span>
      <span class="text-sm text-n-slate-11">
        {{
          t(
            'CAPTAIN_SETTINGS.STATUS.LLM_COUNT',
            { count: records.length },
            records.length
          )
        }}
      </span>
      <span class="text-sm text-n-slate-11">
        {{
          t('CAPTAIN_SETTINGS.STATUS.COVERAGE', {
            covered: coveredCount,
            total: features.length,
          })
        }}
      </span>
    </div>

    <ul v-if="needsAttention" class="flex flex-col gap-1.5 p-0 m-0 list-none">
      <li
        v-for="feature in uncovered"
        :key="feature.key"
        class="flex items-start gap-2 text-sm text-n-slate-12"
      >
        <Icon
          icon="i-lucide-triangle-alert"
          class="flex-shrink-0 size-4 mt-0.5 text-n-amber-11"
        />
        {{
          t('CAPTAIN_SETTINGS.STATUS.UNCOVERED', {
            feature: feature.title,
            provider: providerName(feature.config.provider),
          })
        }}
      </li>
      <li
        v-for="credential in exhausted"
        :key="credential.id"
        class="flex items-start gap-2 text-sm text-n-slate-12"
      >
        <Icon
          icon="i-lucide-gauge"
          class="flex-shrink-0 size-4 mt-0.5 text-n-ruby-11"
        />
        {{ t('CAPTAIN_SETTINGS.STATUS.EXHAUSTED', { name: credential.name }) }}
      </li>
      <li
        v-for="credential in failing"
        :key="`failing-${credential.id}`"
        class="flex items-start gap-2 text-sm text-n-slate-12"
      >
        <Icon
          icon="i-lucide-plug-zap"
          class="flex-shrink-0 size-4 mt-0.5 text-n-ruby-11"
        />
        {{ t('CAPTAIN_SETTINGS.STATUS.FAILING', { name: credential.name }) }}
      </li>
    </ul>
    <p v-else class="flex items-center gap-2 m-0 text-sm text-n-slate-11">
      <Icon icon="i-lucide-circle-check" class="size-4 text-n-teal-11" />
      {{ t('CAPTAIN_SETTINGS.STATUS.ALL_GOOD') }}
    </p>
  </div>
</template>
